import httpService from '../../../core/services/HttpService';
import type {
  InterviewCriteriaTemplate,
  InterviewFormData,
  PhoneInterview,
  PhoneInterviewSubmitData,
  HiringPipeline,
  HiringPipelineCreateData,
} from '../types';

const BASE_URL = '/api/admin/recruiting';

export default class InterviewService {
  static async fetchTemplates(): Promise<InterviewCriteriaTemplate[]> {
    const response = await httpService.get<InterviewCriteriaTemplate[] | { results: InterviewCriteriaTemplate[] }>(
      `${BASE_URL}/interview-templates/`
    );
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && 'templates' in data) return (data as any).templates;
    if (data && typeof data === 'object' && 'results' in data) return (data as any).results;
    return [];
  }

  static async createTemplate(data: Partial<InterviewCriteriaTemplate>): Promise<InterviewCriteriaTemplate> {
    const response = await httpService.post<InterviewCriteriaTemplate>(`${BASE_URL}/interview-templates/`, data);
    return response.data;
  }

  static async getTemplate(id: number): Promise<InterviewCriteriaTemplate> {
    const response = await httpService.get<InterviewCriteriaTemplate>(`${BASE_URL}/interview-templates/${id}/`);
    return response.data;
  }

  static async updateTemplate(id: number, data: Partial<InterviewCriteriaTemplate>): Promise<InterviewCriteriaTemplate> {
    const response = await httpService.patch<InterviewCriteriaTemplate>(`${BASE_URL}/interview-templates/${id}/`, data);
    return response.data;
  }

  static async getInterviewForm(applicantId: number): Promise<InterviewFormData> {
    const response = await httpService.get<InterviewFormData>(
      `${BASE_URL}/applicants/${applicantId}/interview-form/`
    );
    return response.data;
  }

  static async fetchInterviews(applicantId: number): Promise<PhoneInterview[]> {
    const response = await httpService.get<PhoneInterview[] | { results: PhoneInterview[] }>(
      `${BASE_URL}/applicants/${applicantId}/interviews/`
    );
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && 'results' in data) return data.results;
    return [];
  }

  static async submitInterview(applicantId: number, data: PhoneInterviewSubmitData): Promise<PhoneInterview> {
    const response = await httpService.post<PhoneInterview>(
      `${BASE_URL}/applicants/${applicantId}/interviews/`,
      data
    );
    return response.data;
  }

  static async updateInterview(interviewId: number, data: Partial<PhoneInterviewSubmitData>): Promise<PhoneInterview> {
    const response = await httpService.patch<PhoneInterview>(`${BASE_URL}/interviews/${interviewId}/`, data);
    return response.data;
  }

  // --- Templates filtered by work_area ---

  static async fetchTemplatesByWorkArea(workAreaId: number): Promise<InterviewCriteriaTemplate[]> {
    try {
      const response = await httpService.get<InterviewCriteriaTemplate[] | { results: InterviewCriteriaTemplate[] }>(
        `${BASE_URL}/interview-templates/`,
        { params: { work_area: workAreaId } }
      );
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (data && typeof data === 'object' && 'results' in data) return data.results;
      return [];
    } catch {
      return [];
    }
  }

  // --- Hiring Pipeline CRUD ---

  static async fetchPipelines(): Promise<HiringPipeline[]> {
    try {
      const response = await httpService.get<HiringPipeline[] | { results: HiringPipeline[] }>(
        `${BASE_URL}/hiring-pipelines/`
      );
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (data && typeof data === 'object' && 'results' in data) return data.results;
      return [];
    } catch {
      return [];
    }
  }

  static async fetchPipelineByWorkArea(workAreaId: number): Promise<HiringPipeline | null> {
    try {
      const response = await httpService.get<HiringPipeline[] | { results: HiringPipeline[] }>(
        `${BASE_URL}/hiring-pipelines/`,
        { params: { work_area: workAreaId } }
      );
      const data = response.data;
      const arr = Array.isArray(data) ? data : (data as any)?.results || [];
      return arr.length > 0 ? arr[0] : null;
    } catch {
      return null;
    }
  }

  static async createPipeline(data: HiringPipelineCreateData): Promise<HiringPipeline> {
    const response = await httpService.post<HiringPipeline>(`${BASE_URL}/hiring-pipelines/`, data);
    return response.data;
  }

  static async updatePipeline(id: number, data: Partial<HiringPipelineCreateData>): Promise<HiringPipeline> {
    const response = await httpService.patch<HiringPipeline>(`${BASE_URL}/hiring-pipelines/${id}/`, data);
    return response.data;
  }
}
